// src/pages/AdminUserList.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, apiDelete } from "../api/client";
import { AppleComboBox } from "../components/AppleComboBox";
import { Button } from "../components/Button";
import { useParams } from "react-router-dom";
import { formatAnyDateToLocale } from "../utils/dateUtils";
import { A, pillClass } from "../styles/appleClasses";

interface User {
  id: number;
  full_name: string;
  email: string;
  is_ricebook_admin: boolean;
  is_disabled?: boolean;
  last_login?: string | null;
}

type FilterMode = "all" | "admin" | "regular";

export default function AdminUserList() {
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterMode>("all");
  const [searchUserId, setSearchUserId] = useState<number | null>(null);
  const [actionUserId, setActionUserId] = useState<number | null>(null);

  const currentUserId = Number(localStorage.getItem("user_id"));
  const { company_id } = useParams<{ company_id: string }>();

  const fetchUsers = () => {
    setLoading(true);
    apiGet<User[]>(`/admin/users`)
      .then((data) => setAllUsers(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  };

  useEffect(fetchUsers, [company_id]);

  useEffect(() => {
    setSearchUserId(null);
  }, [filter]);

  const handleAction = async (userId: number, action: string) => {
    if (action === "reset-password" && !window.confirm("Send reset link?")) return;
    if (action === "delete" && !window.confirm("Delete this user?")) return;

    setActionUserId(userId);
    try {
      await apiPost(`/admin/users/${userId}/${action}`);
      fetchUsers();
    } finally {
      setActionUserId(null);
    }
  };

  const handleDelete = async (userId: number) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    setActionUserId(userId);
    try {
      await apiDelete(`/admin/users/${userId}`);
      fetchUsers();
    } finally {
      setActionUserId(null);
    }
  };

  const filteredUsers = useMemo(() => {
    return allUsers
      .filter((u) => {
        if (filter === "admin") return u.is_ricebook_admin;
        if (filter === "regular") return !u.is_ricebook_admin;
        return true;
      })
      .filter((u) => (searchUserId ? u.id === searchUserId : true));
  }, [allUsers, filter, searchUserId]);

  const totalLabel = useMemo(() => {
    const base = `Total users: ${allUsers.length}`;
    if (filter === "admin") return `${base} • Admin`;
    if (filter === "regular") return `${base} • Regular`;
    return base;
  }, [allUsers.length, filter]);

  // AppleClasses helpers
  const cardBase = A.card;

  const filterBtnBase =
    `${A.btnBase} h-9 rounded-full px-4 ` +
    "shadow-sm min-w-0 whitespace-nowrap";

  const filterBtnActive = `${filterBtnBase} ${A.btnPrimary}`;
  const filterBtnIdle = `${filterBtnBase} ${A.btnSecondary}`;

  const dangerPill = `${A.pillBase} bg-red-50 text-red-700 ring-red-100`;

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      <div className="max-w-3xl mx-auto px-2 sm:px-6 pt-4 pb-10">
        {/* Header */}
        <div className="mb-3">
          <h2 className={A.title}>Super Admin Panel</h2>
          <div className={`mt-1 ${A.label}`}>{totalLabel}</div>
        </div>

        {/* Controls */}
        <div className={`${cardBase} p-3 sm:p-4 mb-4`}>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            {/* Filter pills */}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFilter("all")}
                className={filter === "all" ? filterBtnActive : filterBtnIdle}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setFilter("admin")}
                className={filter === "admin" ? filterBtnActive : filterBtnIdle}
              >
                Admin
              </button>
              <button
                type="button"
                onClick={() => setFilter("regular")}
                className={filter === "regular" ? filterBtnActive : filterBtnIdle}
              >
                Regular
              </button>
            </div>

            {/* Search */}
            <div className="flex-1 min-w-[240px]">
              <label className={`block mb-1 ${A.label}`}>Search</label>
              <AppleComboBox<User>
                options={allUsers}
                value={allUsers.find((u) => u.id === searchUserId) || null}
                onChange={(u) => setSearchUserId(u ? u.id : null)}
                getOptionLabel={(u) => (u ? `${u.full_name} (${u.email})` : "")}
                placeholder="Search by name or email…"
              />
            </div>
          </div>
        </div>

        {/* List */}
        <div className="space-y-3">
          {loading ? (
            <div className={`${cardBase} px-4 py-3 ${A.label}`}>Loading…</div>
          ) : filteredUsers.length === 0 ? (
            <div className={`${cardBase} px-4 py-3 ${A.label}`}>No users found.</div>
          ) : (
            filteredUsers.map((user) => {
              const isSelf = user.id === currentUserId;
              const busy = actionUserId === user.id;

              return (
                <div key={user.id} className={`${cardBase} p-4`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`${A.title} truncate`}>{user.full_name}</div>

                        {isSelf && <span className={pillClass("neutral")}>You</span>}

                        {user.is_disabled && <span className={dangerPill}>Disabled</span>}

                        {user.is_ricebook_admin ? (
                          <span className={pillClass("brand")}>Admin</span>
                        ) : (
                          <span className={pillClass("neutral")}>User</span>
                        )}
                      </div>

                      <div className={`mt-1 ${A.label} truncate`}>{user.email}</div>

                      <div className={`mt-2 ${A.label}`}>
                        <span className="mr-2">Last login:</span>
                        <span className="text-gray-700">
                          {user.last_login ? formatAnyDateToLocale(user.last_login) : "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  {!isSelf && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {user.is_ricebook_admin ? (
                        <Button
                          type="button"
                          variant="secondary"
                          minWidth="auto"
                          disabled={busy}
                          onClick={() => handleAction(user.id, "remove-admin")}
                          className={[
                            "border border-amber-200 text-amber-800",
                            "hover:bg-amber-50",
                          ].join(" ")}
                        >
                          {busy ? "Working…" : "Demote"}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="secondary"
                          minWidth="auto"
                          disabled={busy}
                          onClick={() => handleAction(user.id, "make-admin")}
                          className={[
                            "border border-green-200 text-green-800",
                            "hover:bg-green-50",
                          ].join(" ")}
                        >
                          {busy ? "Working…" : "Promote"}
                        </Button>
                      )}

                      <Button
                        type="button"
                        variant="secondary"
                        minWidth="auto"
                        disabled={busy}
                        onClick={() => handleAction(user.id, "reset-password")}
                        className="text-appleblue border border-gray-200 hover:bg-gray-50"
                      >
                        {busy ? "Working…" : "Reset Password"}
                      </Button>

                      {user.is_disabled ? (
                        <Button
                          type="button"
                          variant="secondary"
                          minWidth="auto"
                          disabled={busy}
                          onClick={() => handleAction(user.id, "enable")}
                        >
                          {busy ? "Working…" : "Enable"}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="secondary"
                          minWidth="auto"
                          disabled={busy}
                          onClick={() => handleAction(user.id, "disable")}
                        >
                          {busy ? "Working…" : "Disable"}
                        </Button>
                      )}

                      <Button
                        type="button"
                        variant="secondary"
                        minWidth="auto"
                        disabled={busy}
                        onClick={() => handleDelete(user.id)}
                        className="border border-red-200 text-red-700 hover:bg-red-50"
                      >
                        {busy ? "Working…" : "Delete"}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
